import React, { useEffect, useRef, useMemo } from 'react';
import { Chart } from '@antv/g2';
import { ConfigProvider } from 'antd';
import { useTheme } from '../../theme/ThemeContext';
import Select from '../../atoms/Select/Select';
import Button from '../../atoms/Button/Button';
import DownloadFile from '../../atoms/Icon/DownloadFile';
import ArrowRight from '../../atoms/Icon/ArrowRight';
import { WaterfallChartProps, ProcessedWaterfallData } from './WaterfallChart.types';
import { CHART_FONT_FAMILY_CSS } from '../../../utils/formatters';

const WaterfallChart: React.FC<WaterfallChartProps> = (props) => {
    const {
        config,
        title,
        description,
        filters = [],
        footerLabel,
        showFooterArrow = true,
        onDownload,
        onBarClick,
        height = 400,
        positiveColor = '#52C41A',
        negativeColor = '#FF4D4F',
        totalColor = '#0B4266',
        connectorColor = 'rgba(0, 0, 0, 0.25)',
        className = '',
        chartClassName = '',
        showLabels = true,
        showConnectors = true,
        showPercentages = true,
    } = props;

    const theme = useTheme();
    const chartContainer = useRef<HTMLDivElement | null>(null);
    const chartInstance = useRef<Chart | null>(null);

    const { viz_data } = config;
    const chartTitle = title || viz_data.chartTitle;
    const yAxisTitle =
        (Array.isArray(viz_data.yAxisTitles) ? viz_data.yAxisTitles[0] : null) ??
        (typeof (viz_data as { yAxisTitle?: string }).yAxisTitle === 'string'
            ? (viz_data as { yAxisTitle?: string }).yAxisTitle
            : null) ??
        '';

    // Process waterfall data - supports both x as string (API format) and x as string[]
    const processedData = useMemo((): ProcessedWaterfallData[] => {
        const result: ProcessedWaterfallData[] = [];
        const dataPoints = viz_data.data ?? [];

        // Calculate the baseline (first non-zero value)
        let baseline = 0;
        for (const point of dataPoints) {
            const y = point.y;
            if (!Array.isArray(y) || y.length < 2) continue;
            if (y[0] !== 0 || y[1] !== 0) {
                baseline = y[1] !== 0 ? y[1] : y[0];
                break;
            }
        }

        dataPoints.forEach((point, index) => {
            const category = Array.isArray(point.x)
                ? point.x[0]
                : typeof point.x === 'string'
                  ? point.x
                  : `Point ${index + 1}`;
            const y = Array.isArray(point.y) ? point.y : [];
            const start = y[0] ?? 0;
            const end = y[1] ?? 0;
            const value = end - start;

            // Determine type based on position and values
            let type: 'increase' | 'decrease' | 'total';

            // First item or last item (if ends at 0) or starts at 0 -> total
            if (index === 0 || end === 0 || start === 0) {
                type = 'total';
            } else if (value > 0) {
                type = 'increase';
            } else {
                type = 'decrease';
            }

            // Calculate percentage relative to baseline
            const percentage = baseline !== 0 ? (Math.abs(value) / baseline) * 100 : 0;

            result.push({
                category,
                start,
                end,
                value,
                type,
                percentage,
                displayValue: value.toFixed(2),
            });
        });

        return result;
    }, [viz_data.data]);

    // Create connector line data
    const connectorData = useMemo(() => {
        if (!showConnectors) return [];

        const connectors: any[] = [];
        for (let i = 0; i < processedData.length - 1; i++) {
            const current = processedData[i];
            const next = processedData[i + 1];

            connectors.push({
                x: i + 0.45, // Position at end of current bar
                y: current.end,
                x1: i + 0.55, // Position at start of next bar
                y1: next.start,
            });
        }
        return connectors;
    }, [processedData, showConnectors]);

    // Render chart
    useEffect(() => {
        if (!chartContainer.current) return;

        // Destroy previous chart instance
        if (chartInstance.current) {
            chartInstance.current.destroy();
        }

        // Create new chart with top padding so labels above bars are not clipped
        const chart = new Chart({
            container: chartContainer.current,
            autoFit: true,
            paddingTop: 40, // Extra space so value/percentage labels above bars are not clipped
        });

        chart.coordinate({ type: 'cartesian' });

        // Add data
        chart.data(
            processedData.map((d, i) => ({
                ...d,
                index: i,
            })),
        );

        // Configure scales
        chart
            .scale('index', {
                type: 'band',
                paddingInner: 0.3,
                paddingOuter: 0.2,
            })
            .scale('start', { nice: true })
            .scale('end', { nice: true });

        // Add interval (bars) for the waterfall effect
        chart
            .interval()
            .encode('x', 'index')
            .encode('y', (d: ProcessedWaterfallData) => [d.start, d.end])
            .encode('color', (d: ProcessedWaterfallData) => {
                if (d.type === 'total') return 'total';
                if (d.type === 'increase') return 'increase';
                return 'decrease';
            })
            .scale('color', {
                type: 'ordinal',
                domain: ['increase', 'decrease', 'total'],
                range: [positiveColor, negativeColor, totalColor],
            })
            .style('radius', 2)
            .axis('x', {
                title: false,
                labelFormatter: (text: string) => {
                    const idx = Number(text);
                    return !Number.isNaN(idx) && processedData[idx]
                        ? processedData[idx].category
                        : text;
                },
                labelSpacing: 4,
                labelFontFamily: CHART_FONT_FAMILY_CSS,
            })
            .axis('y', {
                title: yAxisTitle,
                titleSpacing: 20,
                grid: true,
                gridStroke: 'rgba(0, 0, 0, 0.06)',
                gridLineWidth: 1,
                labelFontFamily: CHART_FONT_FAMILY_CSS,
                labelFormatter: (value: number) => {
                    if (value >= 1000000) {
                        return `${(value / 1000000).toFixed(2)}M`;
                    } else if (value >= 1000) {
                        return `${(value / 1000).toFixed(2)}K`;
                    }
                    return value.toFixed(0);
                },
            })
            .legend(false)
            .tooltip({
                title: (d: any) => d.category,
                items: [
                    (d: ProcessedWaterfallData) => ({
                        name: 'Value',
                        value: d.value.toLocaleString(),
                    }),
                    (d: ProcessedWaterfallData) => ({
                        name: 'Range',
                        value: `${d.start.toLocaleString()} → ${d.end.toLocaleString()}`,
                    }),
                ],
            });

        // Add labels on top of bars
        if (showLabels) {
            chart
                .text()
                .encode('x', 'index')
                .encode('y', (d: ProcessedWaterfallData) => Math.max(d.start, d.end))
                .encode('text', (d: ProcessedWaterfallData) => {
                    const absValue = Math.abs(d.value);
                    let formattedValue = '';

                    if (absValue >= 1000000) {
                        formattedValue = `${(absValue / 1000000).toFixed(2)}M`;
                    } else if (absValue >= 1000) {
                        formattedValue = `${(absValue / 1000).toFixed(2)}K`;
                    } else {
                        formattedValue = absValue.toFixed(0);
                    }

                    return formattedValue;
                })
                .style('textAlign', 'center')
                .style('textBaseline', 'bottom')
                .style('dy', -5)
                .style('fontSize', 12)
                .style('fill', 'rgba(0, 0, 0, 0.65)')
                .style('fontFamily', CHART_FONT_FAMILY_CSS);

            // Add percentage labels if enabled
            if (showPercentages) {
                chart
                    .text()
                    .encode('x', 'index')
                    .encode('y', (d: ProcessedWaterfallData) => Math.max(d.start, d.end))
                    .encode('text', (d: ProcessedWaterfallData) => {
                        if (d.percentage && d.percentage > 0.01) {
                            return `${d.percentage.toFixed(2)}%`;
                        }
                        return '';
                    })
                    .style('textAlign', 'center')
                    .style('textBaseline', 'bottom')
                    .style('dy', -20)
                    .style('fontSize', 11)
                    .style('fill', 'rgba(0, 0, 0, 0.45)')
                    .style('fontFamily', CHART_FONT_FAMILY_CSS);
            }
        }

        // Add connector lines
        if (showConnectors && connectorData.length > 0) {
            connectorData.forEach((connector) => {
                chart
                    .line()
                    .data([
                        { x: connector.x, y: connector.y },
                        { x: connector.x1, y: connector.y1 },
                    ])
                    .encode('x', 'x')
                    .encode('y', 'y')
                    .style('stroke', connectorColor)
                    .style('strokeWidth', 1)
                    .style('lineDash', [4, 4])
                    .tooltip(false);
            });
        }

        // Handle bar clicks
        if (onBarClick) {
            chart.on('interval:click', (event: any) => {
                onBarClick(event.data?.data);
            });
        }

        chart.render();
        chartInstance.current = chart;

        return () => {
            chart.destroy();
        };
    }, [
        processedData,
        connectorData,
        height,
        positiveColor,
        negativeColor,
        totalColor,
        connectorColor,
        showLabels,
        showConnectors,
        showPercentages,
        yAxisTitle,
        onBarClick,
    ]);

    return (
        <ConfigProvider theme={theme}>
            <div
                className={`w-full bg-white rounded-lg border border-[rgba(0,0,0,0.06)] ${className}`}
                data-test={props['data-test']}
                data-test-id={props['data-test-id']}
                data-asper-waterfall-chart
            >
                {/* Header Section */}
                <div className="p-5 border-b border-[rgba(0,0,0,0.06)]">
                    <div className="flex justify-between items-start mb-2">
                        <h3 className="font-['Open_Sans'] font-semibold text-[16px] leading-normal text-[rgba(0,0,0,0.88)] m-0">
                            {chartTitle}
                        </h3>
                        {onDownload && (
                            <Button
                                type="text"
                                icon={<DownloadFile size={16} />}
                                onClick={onDownload}
                                className="!p-2"
                                data-test-id={`${props['data-test-id']}-download-btn`}
                            />
                        )}
                    </div>

                    {description && (
                        <p className="font-['Open_Sans'] font-normal text-[14px] leading-[22px] text-[rgba(0,0,0,0.65)] m-0">
                            {description}
                        </p>
                    )}
                </div>

                {/* Filters Section */}
                {filters.length > 0 && (
                    <div className="px-5 pt-5 pb-3">
                        <div className="flex flex-wrap gap-4">
                            {filters.map((filter, index) => (
                                <div key={index} className="flex flex-col gap-1">
                                    <label className="font-['Open_Sans'] font-normal text-sm leading-[20px] text-[rgba(0,0,0,0.45)]">
                                        {filter.label}
                                    </label>
                                    <Select
                                        value={filter.defaultValue}
                                        onChange={filter.onChange}
                                        options={filter.options}
                                        className="min-w-[180px]"
                                        data-test-id={`${props['data-test-id']}-filter-${index}`}
                                    />
                                </div>
                            ))}
                        </div>
                    </div>
                )}

                {/* Y-Axis Label */}
                {yAxisTitle && (
                    <div className="px-5 pt-3">
                        <p className="font-['Open_Sans'] font-normal text-sm leading-[20px] text-[rgba(0,0,0,0.45)] m-0">
                            {yAxisTitle}
                        </p>
                    </div>
                )}

                {/* Chart Section */}
                <div className={`px-5 py-5 ${chartClassName}`} style={{ height: `${height}px` }}>
                    <div ref={chartContainer} style={{ width: '100%', height: '100%' }} />
                </div>

                {/* Footer Section */}
                {footerLabel && (
                    <div className="px-5 pb-5 flex justify-center items-center gap-2">
                        <span className="font-['Open_Sans'] font-semibold text-[14px] leading-[22px] text-[rgba(0,0,0,0.88)]">
                            {footerLabel}
                        </span>
                        {showFooterArrow && <ArrowRight size={16} color="rgba(0,0,0,0.88)" />}
                    </div>
                )}
            </div>
        </ConfigProvider>
    );
};

export default WaterfallChart;
